/**
 * "Did you mean …?" for the 404 page.
 *
 * Most visitors who reach PageNotFound followed a URL that never existed. AI
 * assistants (Google AI Mode, ChatGPT) answer "how do I …" with a RoleLogic
 * link they composed from the question rather than one they read —
 * /carl-bot-conditional-roles, /kick-channel-, /integrations/genshin — and old
 * links still point at pages that moved (/plugins/<id>). Every one of those
 * visitors clicked through with an intent the path still spells out, and a
 * site with a handful of referring domains cannot afford to answer it with a
 * dead end.
 *
 * So the path is read as a search query rather than an address: it is split
 * into words, the words that name nothing in particular are dropped, and what
 * is left is compared with the words each real page is called by. A page is
 * suggested only when it wins clearly. A wrong guess takes someone further
 * from what they wanted than no guess at all, so the junk that crawlers probe
 * (/wp-admin, /.env) has to come back empty, and so does the Discord
 * vocabulary that sounds like a page's name without being one: /premium-role
 * is a kind of role, not RoleLogic's plans. suggest-page.test.ts pins both
 * sides with the URLs Search Console and GA4 actually reported.
 *
 * Most pages suggested here are static HTML served outside the SPA, so callers
 * link to them with a plain <a href>: a router <Link> to /discord-… or
 * /integrations/… would route straight back into the 404. The app's own
 * pages (/dashboard, /privacy …) are the exception, and say so (`inApp`).
 *
 * Pure by design (no React, no fetch, no import.meta.env): the component
 * passes in the catalog it has already loaded and where the docs live.
 */

import type { PluginListEntry } from "../model/Model";
// Plain data with no imports, so the browser bundle can share it; the static
// generators build the answer pages from this same list (see HomeLayout.tsx).
import { ANSWER_PAGES } from "../../../scripts/answer-page-registry";

export interface PageLink {
  /**
   * Where the page is: root-relative on this site, or absolute for the docs,
   * which VITE_DOCS_URL can put on another host.
   */
  href: string;
  /** Link text. English, like the pages themselves. */
  label: string;
  /**
   * A route of this app (/dashboard, /privacy), which the router renders.
   * Everything else is served outside the SPA and needs a full page load.
   */
  inApp?: true;
}

export interface PageCandidate extends PageLink {
  /**
   * What the page is called: its slug, its label and any aliases. Each one is
   * compared with the path on its own, and the closest decides — a path that
   * says "exclusive roles" should find the page labelled Exclusive Roles even
   * though its slug says "remove role when role added".
   */
  names: string[];
  /**
   * What kind of page it is. A path about another bot or about Discord itself
   * (/mee6-premium, /discord-status) never wants RoleLogic's own business
   * pages, the docs' plans and support or the app's status and terms.
   */
  kind: "answer" | "integration" | "docs" | "app";
}

/** The docs site's address without a trailing slash. */
const docsBase = (docsUrl: string) => docsUrl.replace(/\/+$/, "");

/**
 * The pages every 404 offers, whatever was asked for. `docsUrl` is where the
 * docs site lives (VITE_DOCS_URL): its own build, served from this host in
 * production and from another in development.
 */
export function siteLinks(docsUrl: string) {
  return {
    home: { href: "/", label: "Home" },
    roleBotGuide: { href: "/discord-role-bot", label: "Discord role bot guide" },
    integrations: { href: "/integrations/", label: "Integrations" },
    docs: { href: `${docsBase(docsUrl)}/`, label: "Documentation" },
    // The app's sign-in lives here too.
    dashboard: { href: "/dashboard", label: "Dashboard", inApp: true },
  } satisfies Record<string, PageLink>;
}

/**
 * Where a path goes when it named a place and no page in it: a section of the
 * site (/plugins, /docs, /login), another bot (/mee6), or RoleLogic itself
 * (/role-logic). There is no page more specific to suggest, and the front
 * door of the right place beats none.
 */
type Place = "home" | "integrations" | "docs" | "dashboard" | "comparison";

/** The answer page that says which bot does what, MEE6 and Carl-bot included. */
const COMPARISON_SLUG = "discord-role-bot-alternative";

/**
 * Other ways people name an answer page, keyed by registry slug. Only
 * phrasings a slug and label cannot reach, and none that another page could
 * claim: an alias is a promise that the phrase means this page, and "carl bot"
 * on the comparison page would pull /carl-bot-conditional-roles away from the
 * conditional-roles page it asked about. (Other bots are places instead; see
 * PLACE_WORDS.)
 */
export const ANSWER_ALIASES: Record<string, string[]> = {
  // The site's own name for what the pillar page explains, and its title's.
  "discord-role-bot": [
    "role automation",
    "automatic roles",
    "automatic role bot",
    "automatic role assignment",
    "assign roles automatically",
    "free role bot",
  ],
  "discord-conditional-roles": [
    "roles based on other roles",
    "role based on another role",
    "if then roles",
  ],
  // "Exclusive" alone is a VIP channel or paid content more often than this.
  "discord-remove-role-when-role-added": ["mutually exclusive roles"],
  // How people describe the problem the troubleshooting page answers.
  "discord-bot-not-assigning-roles": [
    "cant assign role",
    "role not assigned",
    "role not updating",
  ],
  // What boosters get, and what Discord calls them.
  "discord-server-booster-role-bot": ["booster perks", "nitro booster"],
  // What people call the role everyone gets on joining. Made only of weak
  // words (see WEAK), so each matches a path that says exactly this and
  // nothing else: /discord-new-members-role, not /discord-member-role.
  "discord-autorole-bot": [
    "role for new members",
    "join role",
    "welcome role",
    "default role",
  ],
  // Features RoleLogic leaves to other bots: the comparison page says which.
  // And "compare", which is no stem of "comparison" (see wordMatch).
  [COMPARISON_SLUG]: ["reaction roles", "level roles", "compare"],
};

/**
 * Documentation pages worth suggesting by name. Anything under /docs/ gets
 * the docs site's own 404 page, so what reaches this one is a root-level path
 * that named a docs topic: /faq, /getting-started, /pricing, or a guide's slug
 * without its folder (/discord-verification-role-automation). Paths are as the
 * docs build serves them (trailing slashes off), and suggest-page.test.ts
 * checks each against the docs source when it is checked out.
 */
export const DOCS_PAGES: { path: string; label: string; aliases: string[] }[] =
  [
    {
      path: "quick-start",
      label: "Quick start",
      aliases: ["getting started", "setup", "tutorial", "how to use"],
    },
    { path: "faq", label: "FAQ", aliases: ["frequently asked questions"] },
    {
      path: "plans",
      label: "Plans and pricing",
      aliases: ["pricing", "premium", "free plan"],
    },
    {
      path: "support",
      label: "Support",
      aliases: ["help", "contact", "support server"],
    },
    { path: "concepts/rules", label: "IF-THEN rules", aliases: [] },
    { path: "concepts/role-hierarchy", label: "Role hierarchy", aliases: [] },
    {
      path: "features/webhooks-logging",
      label: "Webhook notifications",
      aliases: ["webhooks", "logs", "notifications"],
    },
    {
      path: "guides/common-scenarios",
      label: "Rule examples",
      aliases: [
        "examples",
        "role examples",
        "templates",
        "role templates",
        "use cases",
      ],
    },
    {
      path: "guides/discord-booster-role-rewards",
      label: "Booster role rewards",
      aliases: [],
    },
    {
      path: "guides/discord-verification-role-automation",
      label: "Verification role automation",
      aliases: [],
    },
  ];

/**
 * The app's own pages with an address worth guessing (/privacy-policy,
 * /terms-of-service, /status-page). The dashboard is not one of them: it is
 * a place, reached by naming it alone (/dashbaord, /login).
 */
const APP_PAGES: { path: string; label: string; aliases: string[] }[] = [
  { path: "/privacy", label: "Privacy Policy", aliases: [] },
  // "Terms of use" would otherwise read as "use" and find the quick start.
  { path: "/terms", label: "Terms of Service", aliases: ["terms of use", "tos"] },
  { path: "/status", label: "Status", aliases: ["bot status", "uptime"] },
  { path: "/upgrade", label: "Upgrade", aliases: ["upgrade plan"] },
];

export interface SitePages {
  /** The front door of each place a path can name on its own. */
  places: Record<Place, PageLink>;
  /** Every page a path can name. */
  candidates: PageCandidate[];
}

/**
 * Every page the 404 may suggest: the answer pages, one page per integration
 * in the catalog (the static generator writes /integrations/<plugin id>), the
 * docs pages and the app pages above. Answer pages come first, so the site's
 * own guides win a tie-break over an integration that merely shares a word.
 */
export function sitePages(
  plugins: readonly Pick<PluginListEntry, "id" | "name">[],
  docsUrl: string,
): SitePages {
  const links = siteLinks(docsUrl);
  const comparison = ANSWER_PAGES.find((page) => page.slug === COMPARISON_SLUG);
  return {
    places: {
      home: links.home,
      integrations: links.integrations,
      docs: links.docs,
      dashboard: links.dashboard,
      comparison: {
        href: `/${COMPARISON_SLUG}`,
        label: comparison?.navLabel ?? "Bot comparison",
      },
    },
    candidates: [
      ...ANSWER_PAGES.map((page) => ({
        href: `/${page.slug}`,
        label: page.navLabel,
        names: [page.slug, page.navLabel, ...(ANSWER_ALIASES[page.slug] ?? [])],
        kind: "answer" as const,
      })),
      ...plugins.map((plugin) => ({
        href: `/integrations/${plugin.id}`,
        label: plugin.name,
        names: [plugin.id, plugin.name],
        kind: "integration" as const,
      })),
      ...DOCS_PAGES.map((doc) => ({
        href: `${docsBase(docsUrl)}/${doc.path}`,
        label: doc.label,
        // The last path segment is what gets pasted without its folder, and
        // the whole path what gets pasted without /docs (/concepts/rules).
        names: [
          doc.path.slice(doc.path.lastIndexOf("/") + 1),
          doc.path,
          doc.label,
          ...doc.aliases,
        ],
        kind: "docs" as const,
      })),
      ...APP_PAGES.map((page) => ({
        href: page.path,
        label: page.label,
        inApp: true as const,
        names: [page.path.slice(1), page.label, ...page.aliases],
        kind: "app" as const,
      })),
    ],
  };
}

/* ── words ─────────────────────────────────────────────────────────────── */

/** RoleLogic's own names. A path made only of these wants the front page. */
const BRAND = new Set(["rolelogic", "faizo"]);

/**
 * Words that name a place rather than a page, singular (words are singularised
 * before lookup). A path made only of these wants that place's front door;
 * alongside other words they are just where a page was expected, and say
 * nothing about which: /integrations/genshin wants the Genshin integration,
 * and /carl-bot-conditional-roles asks about conditional roles in Carl-bot's
 * terms. Other bots are places because the comparison page is where RoleLogic
 * meets them; as aliases of it they would outvote the topic next to them.
 * A Map, not an object literal: "/constructor" is not a place.
 */
const PLACE_WORDS = new Map<string, Place>([
  ["integration", "integrations"],
  ["plugin", "integrations"],
  ["doc", "docs"],
  ["documentation", "docs"],
  ["dashboard", "dashboard"],
  // The dashboard is where the app signs people in ("sign in" and "log in"
  // are joined into this word, see COMPOUNDS). "Panel" is not a place: a
  // "role panel" is a self-role message; only /panel alone means the
  // dashboard (see suggestPage).
  ["login", "dashboard"],
  // The bots the comparison page compares RoleLogic with, and their like.
  ["mee6", "comparison"],
  ["carlbot", "comparison"],
  ["dyno", "comparison"],
  ["yagpdb", "comparison"],
  ["zira", "comparison"],
  ["sapphire", "comparison"],
  ["arcane", "comparison"],
  ["tatsu", "comparison"],
  ["probot", "comparison"],
  ["bloxlink", "comparison"],
]);

/**
 * Words every page could claim, and URL furniture. "discord" is in the slug of
 * nearly every page, so an AI-written path that adds or drops it is no nearer
 * to or further from any of them.
 */
const FILLER = new Set([
  "discord",
  "www",
  "com",
  "net",
  "http",
  "https",
  "html",
  "htm",
  "index",
  "page",
  // Where an article would sit on some other site: /blog/discord-autorole-bot
  // and /amp/discord-conditional-roles ask for the page after them.
  "amp",
  "article",
  "blog",
  "guide",
  "kb",
  "learn",
  "news",
  "post",
  "resource",
  "wiki",
]);

/**
 * The glue words AI-written slugs are full of. "not" is not one of them:
 * /roles-not-working means something /roles-working does not. "vs" is: in
 * /rolelogic-vs-mee6 the names on either side say it all.
 */
const STOPWORDS = new Set([
  "across",
  "among",
  "an",
  "and",
  "another",
  "anybody",
  "anyone",
  "are",
  "between",
  "by",
  "can",
  "do",
  "does",
  "everybody",
  "everyone",
  "for",
  "from",
  "give",
  "given",
  "gives",
  "giving",
  "had",
  "has",
  "have",
  "how",
  "in",
  "is",
  "it",
  "my",
  "of",
  "on",
  "or",
  "somebody",
  "someone",
  "that",
  "the",
  "this",
  "to",
  "us",
  "versus",
  "vs",
  "what",
  "when",
  "which",
  "why",
  "with",
  "your",
]);

/**
 * Words too common in Discord role talk to pick a page by themselves. A
 * "premium role", "support role", "notification role", "rules role" or
 * "member role" is a kind of role, never RoleLogic's plans, support, webhook
 * or rules docs, or the Member Origin integration; "role", "bot", "server"
 * and "channel" name half the site. A weak word weighs what a word on every
 * page would, and a page whose only words in common with the path are weak
 * ones is no match at all — unless the path is exactly that page's name:
 * /help, /rules and /premium do mean those pages, and /discord-new-members-role
 * is word for word what the Autorole page answers.
 */
const WEAK = new Set([
  // Discord's own nouns, and the words nearly every page's name has.
  "bot",
  "channel",
  "member",
  "role",
  "server",
  // Who a role is for, or what it does.
  "auto",
  "code",
  "contact",
  "default",
  "help",
  "helper",
  "join",
  "new",
  "notification",
  "ping",
  "premium",
  "rule",
  "staff",
  "support",
  "supporter",
  "temp",
  "top",
  "vip",
  "welcome",
  // A page's one-word name that is just as often part of another phrase:
  // "server templates", "log channel", "plan role", "status role", "accept
  // the terms", "terms of service".
  "log",
  "plan",
  "policy",
  "privacy",
  "service",
  "status",
  "template",
  "term",
  // Words of an integration's or a guide's name that say nothing about which
  // on their own: an "account role", a "content creator role", "Instagram
  // followers", "roles based on invites", "rules examples", "common issues",
  // "setup", "exclusive channels", "role added notifications".
  "account",
  "added",
  "based",
  "common",
  "content",
  "creator",
  "example",
  "exclusive",
  "follower",
  "other",
  "setup",
  "stream",
  "streamer",
  "tutorial",
  // What goes with a topic rather than naming one: a "role hierarchy fix",
  // roles removed "automatically", "multi-server" sync, a Twitch "sub" role.
  "automatic",
  "automatically",
  "fix",
  "multi",
  "multiple",
  "sub",
  // What a game or platform role is about, whichever game or platform:
  // /discord-steam-game-role is Steam's, /discord-roblox-group-roles
  // Roblox's, /discord-osu-rank-role osu!'s; alone they name none of them.
  "game",
  "group",
  "level",
  "player",
  "rank",
]);

/**
 * Words that name a page only in the right company. "Kick" is the Kick.com
 * integration when the path talks streaming (a channel, followers, subs, a
 * Kick role) and the moderation verb otherwise: /discord-auto-kick and
 * /discord-kick-inactive-members are not about the streaming site. Out of
 * that company the word counts as weak (see WEAK).
 */
const HOMONYMS = new Map<string, RegExp>([
  [
    "kick",
    /kick[^a-z0-9]*com|\b(?:channel|stream(?:er|ing)?|live|follow(?:er)?s?|subs?|subscri[a-z]*|vips?|og|gift[a-z]*|roles?)\b/,
  ],
]);

/**
 * Names of more than one word that are one word on this site. Left apart,
 * "role logic" would score as two real words and "auto role" would be
 * half-matched by "automation"; joined, they are the brand and the autorole
 * page's own term. "Carl-bot" is a bot's name, not a bot; "sign in" is where
 * the dashboard is; and "terms and conditions" are the terms, not the
 * conditional-roles page.
 */
const COMPOUNDS: [RegExp, string][] = [
  [/role[^a-z0-9]*logic/g, " rolelogic "],
  [/auto[^a-z0-9]*role/g, " autorole"],
  [/\bcarl[^a-z0-9]*bots?\b/g, " carlbot "],
  [/\b(?:log|sign)[^a-z0-9]*in\b/g, " login "],
  [/\bterms?[^a-z0-9]*(?:and[^a-z0-9]*)?conditions?\b/g, " tos "],
];

/**
 * The words that mean the same whatever pages exist — the brand, the place
 * names and "discord" — are also recognised in a path with a typo in them:
 * /dashbaord, /intergrations/genshin, /discrod-conditional-roles. (A page's
 * own words are forgiven typos when they are compared; see wordMatch.) The
 * weak words are not: "automation" is two edits from "automatic", and a page
 * word read as a weak one would stop naming its page.
 */
const FIXED_WORDS = [...BRAND, ...PLACE_WORDS.keys(), "discord"];

/**
 * Longest path worth reading. Nobody mistypes 200 characters of page name,
 * and a bound keeps a hostile URL from costing anything.
 */
const MAX_PATH_LENGTH = 200;

/**
 * Percent-decodes without throwing. decodeURIComponent rejects the whole
 * string over one malformed escape (/%E0%A4%A), so on failure each run of
 * escapes is decoded on its own and a run that is not valid UTF-8 becomes a
 * word break.
 */
function decodePath(raw: string): string {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw.replace(/(?:%[0-9a-f]{2})+/gi, (run) => {
      try {
        return decodeURIComponent(run);
      } catch {
        return " ";
      }
    });
  }
}

/**
 * Plural to singular, crudely: "roles" → "role", "boosters" → "booster",
 * "categories" → "category". Both sides of every comparison go through it, so
 * it only has to be consistent, not correct ("status" and "access" are left
 * alone because they are not plurals).
 */
function singular(word: string): string {
  if (word.length > 4 && word.endsWith("ies")) return `${word.slice(0, -3)}y`;
  if (word.length > 3 && /[^su]s$/.test(word) && !word.endsWith("is"))
    return word.slice(0, -1);
  return word;
}

interface Words {
  /** The words that could name a page, in order, without repeats. */
  words: string[];
  /** The last place named (/docs/plugins/x is an integrations path). */
  place: Place | null;
  /** Whether RoleLogic itself was named. */
  brand: boolean;
  /** Whether another bot was named (a comparison place word). */
  competitor: boolean;
  /** Whether Discord itself was named: /discord-status is not ours. */
  discord: boolean;
  /**
   * The last word kept, if its text has MIN_CUT letters or more: a link
   * copied short stops mid-word (/integrations/youtube-sub). A trailing slash
   * or filler after it (/youtube-sub/, /youtube-sub.html) does not change it.
   */
  cut: string | null;
}

/**
 * Splits a path, or a page's name, into comparable words: lowercased, accents
 * dropped, compounds joined, split on anything that is not a letter or digit,
 * singularised, and with the brand, place names, filler, glue words, bare
 * numbers and single letters taken out. Anything outside a-z (other scripts,
 * emoji) becomes a word break: none of the pages is named in it, so it cannot
 * count for one. `typos` reads a misspelt fixed word as that word; only a
 * path gets it, so that no page's own word can be mistaken for a place.
 */
function toWords(text: string, typos = false): Words {
  const words: string[] = [];
  let place: Place | null = null;
  let brand = false;
  let competitor = false;
  let discord = false;
  let cut: string | null = null;
  // NFKD splits "é" into "e" and a combining accent; the accent (a Unicode
  // mark) is then dropped.
  let plain = text.toLowerCase().normalize("NFKD").replace(/\p{M}/gu, "");
  for (const [phrase, word] of COMPOUNDS) plain = plain.replace(phrase, word);
  for (const token of plain.split(/[^a-z0-9]+/)) {
    if (token.length < 2 || /^\d+$/.test(token)) continue;
    let word = singular(token);
    if (typos && !PLACE_WORDS.has(word) && !BRAND.has(word)) {
      word = FIXED_WORDS.find((fixed) => isTypo(word, fixed)) ?? word;
    }
    const placeNamed = PLACE_WORDS.get(word);
    if (word === "discord") discord = true;
    // "RoleLogic vs other bots" and "<any bot> alternative" are comparisons
    // even when the bot is not one PLACE_WORDS names.
    if (word === "vs" || word === "versus" || word === "alternative") {
      competitor = true;
    }
    if (BRAND.has(word)) brand = true;
    else if (placeNamed) {
      place = placeNamed;
      if (placeNamed === "comparison") competitor = true;
    } else if (!FILLER.has(word) && !STOPWORDS.has(word)) {
      if (!words.includes(word)) words.push(word);
      cut = token.length >= MIN_CUT ? word : null;
    }
  }
  return { words, place, brand, competitor, discord, cut };
}

/** Letters a word needs before one typo in it is forgiven. */
const TYPO_ONE = 6;
/** Letters a word needs before two typos in it are forgiven. */
const TYPO_TWO = 9;

/**
 * Whether one word is the other mistyped: a letter dropped, added, changed or
 * swapped with the next, once in a word of TYPO_ONE letters and twice in one
 * of TYPO_TWO. The first letter has to agree — typos rarely land there, and
 * without the rule "inclusive" would be two typos from "exclusive". A word
 * shorter than TYPO_TWO may not differ by one changed letter alone: that is
 * how English words differ from each other (remote and remove, lodging and
 * logging, prayer and player), and a typo is more often a letter dropped,
 * doubled or swapped (condtional, dashbord, tiwtch).
 */
function isTypo(a: string, b: string): boolean {
  const shorter = Math.min(a.length, b.length);
  if (shorter < TYPO_ONE || a[0] !== b[0]) return false;
  if (shorter < TYPO_TWO && a.length === b.length) {
    // Words of one length one edit apart differ by a changed letter or by a
    // swap of neighbours; only the swap counts here.
    const i = [...a].findIndex((letter, j) => letter !== b[j]);
    return (
      i >= 0 &&
      a[i] === b[i + 1] &&
      a[i + 1] === b[i] &&
      a.slice(i + 2) === b.slice(i + 2)
    );
  }
  const allowed = shorter >= TYPO_TWO ? 2 : 1;
  return (
    Math.abs(a.length - b.length) <= allowed &&
    editDistance(a, b, allowed) <= allowed
  );
}

/**
 * Edits from one word to the other, a swap of neighbours counting as one
 * (Damerau's optimal string alignment distance). Gives up, returning more
 * than `max`, as soon as every alignment has passed it.
 */
function editDistance(a: string, b: string, max: number): number {
  let twoBack: number[] = [];
  let previous = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++) {
      row[j] = Math.min(
        previous[j] + 1,
        row[j - 1] + 1,
        previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1])
        row[j] = Math.min(row[j], twoBack[j - 2] + 1);
    }
    if (Math.min(...row) > max) return max + 1;
    twoBack = previous;
    previous = row;
  }
  return previous[b.length];
}

/**
 * Letters a stem needs before a longer word counts as it with an ending:
 * "boost" / "booster". Four would make "temp" a stem of "templates" and
 * "help" one of "helper".
 */
const MIN_STEM = 5;
/**
 * How much of the longer word the stem has to be: "condition" is 9 of
 * "conditional"'s 11 letters, "support" 7 of "supporter"'s 9.
 */
const STEM_SHARE = 0.6;
/**
 * Stems too short a part of their long forms for STEM_SHARE that still mean
 * one thing on this site: "verify" and "verification", "boosting" and
 * "booster", "subscription" and "subscriber".
 */
const SHORT_STEMS = ["verif", "boost", "subscri"];
/** Letters a word needs to count when found inside another one. */
const MIN_INNER = 4;
/**
 * Letters the last word of a path needs before it counts as the start of a
 * page's word, "sub" of "subscriber". Fewer is too little to guess from.
 */
const MIN_CUT = 3;
/**
 * Credit for a stem and a longer form of it, and for a word cut short (see
 * Words.cut). Under 3/4, like TYPO_MATCH; see MIN_SCORE.
 */
const STEM_MATCH = 0.7;
/** Credit for a word mistyped (see isTypo). */
const TYPO_MATCH = 0.7;
/** Credit for one word inside another ("role" in "autorole"). */
const INNER_MATCH = 0.5;

/**
 * How far a word of the path is a word of a page's name: 1 when equal,
 * partial credit when it is a stem of it, a typo of it, or sits inside it, 0
 * otherwise. "twitter" shares its first four letters with "twitch" and is none
 * of those. A stem or a part counts one way only, the path's word inside the
 * page's ("condition" of "conditional", "role" of "autorole"): the other way
 * round a longer word merely contains a page's word, and "original" is no
 * form of "origin", nor "account" of "count".
 */
function wordMatch(pathWord: string, pageWord: string): number {
  if (pathWord === pageWord) return 1;
  if (
    pathWord.length >= MIN_STEM &&
    pageWord.startsWith(pathWord) &&
    pathWord.length >= pageWord.length * STEM_SHARE
  )
    return STEM_MATCH;
  if (
    SHORT_STEMS.some(
      (stem) => pathWord.startsWith(stem) && pageWord.startsWith(stem),
    )
  )
    return STEM_MATCH;
  if (isTypo(pathWord, pageWord)) return TYPO_MATCH;
  return pathWord.length >= MIN_INNER && pageWord.includes(pathWord)
    ? INNER_MATCH
    : 0;
}

/**
 * The share of `from`'s weight that `to` accounts for, 0..1. Each word counts
 * its best match on the other side.
 */
function coverage(
  from: string[],
  to: string[],
  weight: (word: string) => number,
  match: (word: string, other: string) => number,
): number {
  let total = 0;
  let found = 0;
  for (const word of from) {
    const w = weight(word);
    total += w;
    found += w * Math.max(0, ...to.map((other) => match(word, other)));
  }
  return total > 0 ? found / total : 0;
}

/**
 * The score a suggestion has to beat, 0..1 — beat, not reach: one fully
 * matched word beside a word no page uses, against a two-word name, lands on
 * exactly 0.5 (/discord-common-questions), and that is a guess. A path whose
 * best page shares
 * only part of a word with it lands under it, with no other page anywhere
 * near: /auto-assign-roles scores 0.47 for the roles-not-assigned fix
 * ("assign" starts "assigning"), /discord-member-count-role 0.39 for Bluesky
 * Account Role ("count" is inside "account"). Partial credit is kept under
 * 3/4 so that one part-matched word beside a word no page uses, or against a
 * two-word name, lands under it too: 2 × 0.7 / 3 = 0.47 (/verify/omerthedev,
 * /lodging). The weakest reported URL, /role-sync, scores 0.67, and the
 * weakest the tests pin, /twitch-bot, 0.59.
 */
const MIN_SCORE = 0.5;

/**
 * How far the winner has to lead the next page. A near tie means the path
 * fits two pages about equally — /discord-reward-roles fits the booster
 * rewards guide and Loot Labs Reward Role 0.04 apart, and
 * /discord-steam-verification the Verification page and Steam Player Role
 * 0.07 apart — and picking one is a coin toss the visitor loses half the
 * time. The closest lead the tests pin is 0.10: /if-then-roles is the
 * conditional-roles page's alias, and the IF-THEN rules docs are next.
 */
const MIN_LEAD = 0.08;

/**
 * Every candidate scored against the path's words, best first.
 *
 * A path and a name are compared both ways — how much of the path the name
 * explains, and how much of the name the path spelled out — and the two are
 * combined as a harmonic mean, so a page has to do well on both: "/genshin" is
 * all of the path and half the name of Genshin Player Role, while "/role" is
 * all of the path but a small part of any page's name. Words are weighted by
 * rarity across the candidates (inverse document frequency): a word that names
 * one page decides a match, and a word many pages share barely moves one; a
 * weak word (see WEAK) weighs what a word on every page would, whatever its
 * count. A word no page uses weighs as much as the rarest, so an unknown word
 * dilutes the match instead of being ignored.
 */
function rank(
  { words, cut, place }: Words,
  candidates: readonly PageCandidate[],
  isWeak: (word: string) => boolean,
): { candidate: PageCandidate; score: number }[] {
  // How far a word of the path is a word of a name: as wordMatch, except that
  // the word a path was cut short on also counts as the start of a longer one
  // — when the path says something else too: "con" alone could be the start
  // of half the site.
  const cutShort = words.length > 1 ? cut : null;
  const match = (word: string, other: string) =>
    word === cutShort && other.length > word.length && other.startsWith(word)
      ? Math.max(STEM_MATCH, wordMatch(word, other))
      : wordMatch(word, other);
  const named = candidates.map((candidate) =>
    candidate.names.map((name) => toWords(name).words),
  );
  const pagesWith = new Map<string, number>();
  for (const names of named) {
    for (const word of new Set(names.flat())) {
      pagesWith.set(word, (pagesWith.get(word) ?? 0) + 1);
    }
  }
  const weight = (word: string) =>
    Math.log(
      1 +
        (isWeak(word) ? 1 : candidates.length / (pagesWith.get(word) ?? 1)),
    );
  // Residue is not something the path is about: it still dilutes the score,
  // but no page is asked to account for it. Only a word no page uses can be
  // residue ("gg" is Top.gg's): a two-letter one everywhere (/pt-br/…,
  // /discord-autorole-bo), a three-letter one under /integrations/, where it
  // is an id segment (/integrations/tid/…) — anywhere else it is a real word,
  // "ban" in /discord-cross-server-ban. The word a link was cut short on is
  // never residue: it is what the link was reaching for.
  const strong = words.filter(
    (word) =>
      !isWeak(word) &&
      (word === cutShort ||
        pagesWith.has(word) ||
        (word.length > 2 &&
          (word.length >= 4 || place !== "integrations"))),
  );

  return (
    candidates
      .map((candidate, i) => {
        let score = 0;
        for (const name of named[i]) {
          const explained = coverage(words, name, weight, match);
          const spelled = coverage(name, words, weight, (a, b) => match(b, a));
          if (explained + spelled === 0) continue;
          const fit = (2 * explained * spelled) / (explained + spelled);
          // Short of the page's exact name, a page has to account for what
          // the path is about: every strong word in it, or at least two. One
          // strong word in common is not enough when the path says something
          // else too — /discord-kick-members-without-role shares "kick" with
          // the Kick integration and asks about something it cannot do, and
          // /discord-account-age-role shares "account" with Bluesky Account
          // Role. Weak words alone are never a match (see WEAK).
          // A strong word has to find a strong one — except the word a link
          // was cut on, whose whole may be weak (/discord-cross-serv).
          const matched = strong.filter((word) =>
            name.some(
              (other) =>
                (!isWeak(other) || word === cutShort) && match(word, other) > 0,
            ),
          ).length;
          const accounted =
            matched >= 2 || (matched > 0 && matched === strong.length);
          if (fit < 1 && !accounted) continue;
          score = Math.max(score, fit);
        }
        return { candidate, score };
      })
      // Stable: on equal scores the earlier candidate (an answer page) stays
      // first.
      .sort((a, b) => b.score - a.score)
  );
}

/** A path ending, or a segment ending, in a server-side script extension. */
const SCRIPT_ENDINGS =
  /\.(?:php\d?|aspx?|jsp|cgi|pl|cfm|env|sql|bak|ini)(?:$|[/.])/i;

/**
 * The page a mistyped, invented or outdated path most likely meant, or null
 * when nothing fits well enough to put in front of a visitor.
 */
export function suggestPage(path: string, site: SitePages): PageLink | null {
  // A second address pasted onto the first (…role-addedhttps://rolelogic…)
  // is read as a word break, not glued to the word before it.
  const address = path
    .split(/[?#]/, 1)[0]
    .slice(0, MAX_PATH_LENGTH)
    .replace(/https?:\/\/[^/]*/gi, " ");
  // Server scripts are what scanners probe for (/upgrade.php, /faq.asp); no
  // page here ever had one of these endings.
  if (SCRIPT_ENDINGS.test(address)) return null;
  const decoded = decodePath(address);
  const query = toWords(decoded, true);
  // Another bot's name outranks the dashboard as the place: /mee6-dashboard
  // is MEE6's, and the comparison page is where RoleLogic meets it.
  const place = query.competitor
    ? site.places.comparison
    : query.place
      ? site.places[query.place]
      : query.brand
        ? site.places.home
        : null;
  if (!place && query.words.length === 1 && query.words[0] === "panel") {
    return site.places.dashboard;
  }

  const plain = decoded.toLowerCase();
  const isWeak = (word: string) => {
    if (WEAK.has(word)) return true;
    const company = HOMONYMS.get(word);
    return !!company && query.place !== "integrations" && !company.test(plain);
  };
  // About another bot, or about Discord itself, a path never wants
  // RoleLogic's own business pages: /mee6-premium is not our plans, and
  // /discord-status or /discord-tos not our status or terms.
  const candidates = site.candidates.filter(
    (candidate) =>
      !(query.competitor && (candidate.kind === "docs" || candidate.kind === "app")) &&
      !(query.discord && candidate.kind === "app"),
  );

  if (query.words.length > 0) {
    const [best, next] = rank(query, candidates, isWeak);
    if (
      best &&
      best.score > MIN_SCORE &&
      (!next || best.score - next.score >= MIN_LEAD)
    ) {
      const { href, label, inApp } = best.candidate;
      return inApp ? { href, label, inApp } : { href, label };
    }
    // Another bot named beside words no page here answers: the comparison
    // page is still where that visitor's question meets RoleLogic.
    if (query.competitor) return place;
    // An unknown word asked for a page this site does not have
    // (/integrations/undefined), not for the place it was expected in. Weak
    // words ask for nothing in particular (/integrations/role, /mee6-bot).
    if (!query.words.every(isWeak)) return null;
  }
  return place;
}
